/**
 * "Join my survey" — the one use case every behavior channel shares: join the
 * channel's data with one of the user's surveys and check what respondents
 * said against what they actually did. Pure, no React.
 *
 * Every channel asks the same three questions (`questions.ts`): which survey,
 * which of its questions, and which window of behavior counts. Only the words
 * differ, so each channel supplies its `JoinWords` and gets its action from
 * `joinSurveyAction`. Attitudes is the exception: psychographics are a profile
 * rather than dated behavior, so it has no window question.
 *
 * DANI resolves the survey from its `NNNNN-Name` reference in an ordinary chat
 * question (checked on test1 against 33002-AT&T Retail Tracker May25: it found
 * the project and its questions and read the window as dates).
 */

import type { ConnectChannelAction, JoinChannelKey } from './types';

export const JOIN_SURVEY_ID_SUFFIX = 'join-survey';

/** How a channel's data reads in the questions, the summary and the prompt. */
export interface JoinWords {
    /** "Join {data} with survey …": 'visit data'. */
    data: string;
    /** "compare respondents' answers … with {actual}": 'where they actually went'. */
    actual: string;
    /** "Which answers should DANI check against {evidence}?": 'their real store visits'. */
    evidence: string;
    /** "counting {behavior} …" and "Which {behavior} should count?": 'visits'.
     * Omitted for a channel with no dated behavior (Attitudes). */
    behavior?: string;
}

export const JOIN_WORDS: Record<JoinChannelKey, JoinWords> = {
    location: {
        data: 'visit data',
        actual: 'where they actually went',
        evidence: 'their real store visits',
        behavior: 'visits',
    },
    app: {
        data: 'app usage data',
        actual: 'the apps they actually used',
        evidence: 'their real app usage',
        behavior: 'app usage',
    },
    web: {
        data: 'web browsing data',
        actual: 'the sites they actually visited',
        evidence: 'their real browsing',
        behavior: 'browsing',
    },
    purchase: {
        data: 'purchase data',
        actual: 'what they actually bought',
        evidence: 'their real purchases',
        behavior: 'purchases',
    },
    'digital-ads': {
        data: 'ad exposure data',
        actual: 'the ads they actually saw',
        evidence: 'the ads they really saw',
        behavior: 'ad exposure',
    },
    media: {
        data: 'media viewing data',
        actual: 'what they actually watched',
        evidence: 'what they really watched',
        behavior: 'viewing',
    },
    audio: {
        data: 'audio listening data',
        actual: 'what they actually listened to',
        evidence: 'what they really listened to',
        behavior: 'listening',
    },
    'gen-ai-chatgpt': {
        data: 'ChatGPT conversation data',
        actual: 'what they actually asked ChatGPT',
        evidence: 'their real ChatGPT conversations',
        behavior: 'ChatGPT conversations',
    },
    'gen-ai-gemini': {
        data: 'Gemini conversation data',
        actual: 'what they actually asked Gemini',
        evidence: 'their real Gemini conversations',
        behavior: 'Gemini conversations',
    },
    attitudes: {
        data: 'psychographic profiles',
        actual: 'their attitudes',
        evidence: 'their attitudes',
    },
};

/** The default window: behavior around when they answered. */
export const RECOMMENDED_JOIN_WINDOW = 'from 4 weeks before the survey through fielding';

export const JOIN_WINDOW_OPTIONS = [
    {
        value: RECOMMENDED_JOIN_WINDOW,
        label: '4 weeks before the survey through fielding',
        description: 'Behavior around when they answered',
    },
    {
        value: 'in the 12 weeks before the survey',
        label: '12 weeks before the survey',
        description: 'Habits before they answered',
    },
    {
        value: 'since the survey closed',
        label: 'Since the survey closed',
        description: 'Whether they followed through',
    },
] as const;

/** What the survey slot prints when the user lets DANI list their surveys. A
 * picked survey prints as "my survey NNNNN-Name" (`questions.ts`). */
const UNPICKED_SURVEY = 'one of my surveys (list them first so I can pick)';

function templateFor(words: JoinWords): string {
    if (!words.behavior) {
        return (
            `Join ${words.data} with {survey|${UNPICKED_SURVEY}}: do respondents' attitudes ` +
            `explain their answers to {question|the survey's key questions}?`
        );
    }
    return (
        `Join ${words.data} with {survey|${UNPICKED_SURVEY}}: compare respondents' answers to ` +
        `{question|the questions that best match ${words.data}} with ${words.actual}, ` +
        `counting ${words.behavior} {joinWindow|${RECOMMENDED_JOIN_WINDOW}}.`
    );
}

function summaryFor(words: JoinWords): string {
    return words.behavior
        ? `whether what your respondents said matches ${words.actual}, using ${words.evidence}`
        : `whether your respondents' attitudes explain how they answered your survey`;
}

/** A channel's "Join my survey" use case. */
export function joinSurveyAction(channel: JoinChannelKey): ConnectChannelAction {
    const words = JOIN_WORDS[channel];
    return {
        id: `${channel}-${JOIN_SURVEY_ID_SUFFIX}`,
        name: 'Join my survey',
        icon: 'clipboard-list',
        template: templateFor(words),
        summary: summaryFor(words),
    };
}
